import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertCircle, User, Mail, Phone, Building2 } from 'lucide-react'
import { ApiService } from '@/services/api'
import type { User as UserType, Restaurant } from '@/types'

interface CreateUserFormProps {
  open: boolean
  onClose: () => void
  onSuccess: (user: UserType) => void
}

interface FormData {
  email: string
  password: string
  confirmPassword: string
  name: string
  phone: string
  userType: 'admin' | 'restaurant_owner'
  restaurantIds: string[]
}

const initialFormData: FormData = {
  email: '',
  password: '',
  confirmPassword: '',
  name: '',
  phone: '',
  userType: 'restaurant_owner',
  restaurantIds: []
}

export default function CreateUserForm({ open, onClose, onSuccess }: CreateUserFormProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      loadRestaurants()
    }
  }, [open])

  const loadRestaurants = async () => {
    setIsLoadingRestaurants(true)
    try {
      const response = await ApiService.getAllRestaurants()
      if (response.success) {
        setRestaurants(response.data)
      }
    } catch (err) {
      console.error('Failed to load restaurants:', err)
    } finally {
      setIsLoadingRestaurants(false)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.name || !formData.phone) {
      return 'Please fill in all required fields'
    }

    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match'
    }

    if (formData.password.length < 6) {
      return 'Password must be at least 6 characters long'
    }

    if (formData.userType === 'restaurant_owner' && formData.restaurantIds.length === 0) {
      return 'Please select at least one restaurant for restaurant owner'
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address'
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        userType: formData.userType,
        restaurantIds: formData.userType === 'restaurant_owner' ? formData.restaurantIds : [],
        isActive: true
      }

      const response = await ApiService.createUser(userData)
      
      if (response.success) {
        onSuccess(response.data)
        setFormData(initialFormData)
        onClose()
      } else {
        setError(response.message || 'Failed to create user')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setError(null)
    setFormData(initialFormData)
    onClose()
  }

  const toggleRestaurant = (restaurantId: string) => {
    setFormData(prev => ({
      ...prev,
      restaurantIds: prev.restaurantIds.includes(restaurantId)
        ? prev.restaurantIds.filter(id => id !== restaurantId)
        : [...prev.restaurantIds, restaurantId]
    }))
  }

  const selectedRestaurants = restaurants.filter(r => formData.restaurantIds.includes(r._id))

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <User className="h-5 w-5" />
            <span>Create New User</span>
          </DialogTitle>
          <DialogDescription>
            Add a new user account to the system
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* User Type */}
          <div className="space-y-2">
            <Label htmlFor="userType">User Type *</Label>
            <Select
              value={formData.userType}
              onValueChange={(value) => handleInputChange('userType', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin User</SelectItem>
                <SelectItem value="restaurant_owner">Restaurant Owner</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+919876543210"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="user@example.com"
              required
            />
          </div>

          {/* Restaurant Selection for Restaurant Owners — select multiple to
              create a "Head of Restaurant" account (owns more than one restaurant) */}
          {formData.userType === 'restaurant_owner' && (
            <div className="space-y-2">
              <Label>Restaurants * {formData.restaurantIds.length > 1 && (
                <span className="text-xs font-normal text-muted-foreground">(Head of Restaurant — {formData.restaurantIds.length} selected)</span>
              )}</Label>
              <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                {isLoadingRestaurants ? (
                  <div className="p-3 text-sm text-muted-foreground">Loading restaurants...</div>
                ) : (
                  restaurants.map((restaurant) => (
                    <label
                      key={restaurant._id}
                      className="flex items-center space-x-2 p-2.5 cursor-pointer hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={formData.restaurantIds.includes(restaurant._id)}
                        onChange={() => toggleRestaurant(restaurant._id)}
                        className="rounded"
                      />
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm">{restaurant.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedRestaurants.length > 0 && (
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {selectedRestaurants.map(r => (
                    <p key={r._id}>{r.name} — Owner: {r.ownerName} | Database: {r.databaseName}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Password */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Minimum 6 characters"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Confirm password"
                required
              />
            </div>
          </div>

          {/* User Type Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              {formData.userType === 'admin' ? (
                <>
                  <strong>Admin User:</strong> Full access to admin dashboard, restaurant management, 
                  subscriptions, and all system features.
                </>
              ) : (
                <>
                  <strong>Restaurant Owner:</strong> Access to restaurant-specific dashboard, 
                  analytics, reports, and subscription management for their restaurant.
                </>
              )}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || isLoadingRestaurants}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Creating User...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}